const formatDateForPrint = (value) => {
  if (!value) return "";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB");
};

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/\"/g, "&quot;")
  .replace(/'/g, "&#39;");

const numberToWords = (value) => {
  const num = Math.floor(Number(value) || 0);
  if (!num) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigits = (n) => n < 10 ? ones[n] : n < 20 ? teens[n - 10] : `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${ones[n % 10]}` : ""}`.trim();
  const threeDigits = (n) => n < 100 ? twoDigits(n) : `${ones[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${twoDigits(n % 100)}` : ""}`.trim();
  const parts = [];
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const hundred = num % 1000;
  if (crore) parts.push(`${threeDigits(crore)} Crore`);
  if (lakh) parts.push(`${threeDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigits(thousand)} Thousand`);
  if (hundred) parts.push(threeDigits(hundred));
  return parts.join(" ").trim();
};

export const buildBillHtml = ({ quotation, customer, branding, items, assetBaseUrl, showPackagingCharges = true }) => {
  const quotationDate = formatDateForPrint(quotation.created_at) || formatDateForPrint(new Date());
  const cssUrl = `${assetBaseUrl}/css/billPrint.css`;
  const phone = [branding.phone, branding.whatsapp].filter(Boolean);
  const subtotal = Number(quotation.subtotal_amount || 0);
  const discount = Number(quotation.discount_amount || 0);
  const packaging = Number(quotation.packaging_charges || 0);
  const transport = Number(quotation.transport_charges || 0);
  const grossAmount = Number(quotation.gross_amount || (subtotal - discount) || 0);
  const cgstAmount = Number(quotation.cgst_amount || 0);
  const sgstAmount = Number(quotation.sgst_amount || 0);
  const igstAmount = Number(quotation.igst_amount || 0);
  const grandTotal = Number(quotation.grand_total || 0);
  const amountWords = numberToWords(grandTotal);
  const totalQuantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>
      Tax Invoice ${escapeHtml(
        quotation.bill_number || quotation.quotation_number || ""
      )}
    </title>

    <link rel="stylesheet" href="${cssUrl}" />
  </head>

  <body>
    <div class="bill-sheet">
      <div class="bill-topline">
        <span>Cash/Credit Memo</span>
        <span>TAX INVOICE</span>
      </div>

      <div class="bill-gstin">
        GSTIN No. : ${escapeHtml(branding.ownerGstNumber || "—")}
      </div>

      <div class="bill-brand">
        <div class="bill-brand-name">
          ${escapeHtml(branding.brandName || "NILAM SYNTHETICS")}
        </div>

        <div class="bill-brand-address">
          ${escapeHtml(branding.address || "")}
        </div>

        <div class="bill-contact">
          ${phone
            .map((n) => `(M) ${escapeHtml(n)}`)
            .join("<br/>")}
        </div>
      </div>

      <div class="bill-meta">
        <div class="bill-meta-row">
          <div class="bill-meta-cell">
            <b>Name :</b>
            ${escapeHtml(quotation.customer_name || "")}
          </div>

          <div class="bill-meta-cell">
            <b>Invoice No. :</b>
            ${escapeHtml(quotation.bill_number || "")}
          </div>

          <div class="bill-meta-cell">
            <b>Date :</b>
            ${escapeHtml(quotationDate)}
          </div>
        </div>

        <div class="bill-meta-row">
          <div class="bill-meta-cell">
            <b>Address :</b>
            ${escapeHtml(
              (quotation.customer_address || customer.address || "") +
              (quotation.customer_city || customer.city ? ", " + (quotation.customer_city || customer.city) : "") +
              (quotation.customer_pincode || customer.pincode ? " - " + (quotation.customer_pincode || customer.pincode) : "")
            )}
          </div>

          <div class="bill-meta-cell">
            <b>M. No. :</b>
            ${escapeHtml(quotation.customer_mobile || "")}
          </div>

          <div class="bill-meta-cell">
            <b>Challan No. :</b>
            ${escapeHtml(quotation.quotation_number || "")}
          </div>
        </div>

        <div class="bill-meta-row">
          <div class="bill-meta-cell">
            <b>GSTIN :</b>
            ${escapeHtml(customer.gst_number || "")}
          </div>

          <div class="bill-meta-cell">
            <b>State :</b>
            ${escapeHtml(customer.state || "")}
          </div>

          <div class="bill-meta-cell">
            <b>Transport :</b>
            ${escapeHtml(quotation.transport_name || customer.transport_name || "")}
          </div>
        </div>
      </div>

      <table class="bill-table${items.length > 9 ? ' compact' : ''}">
        <thead>
          <tr>
            <th style="width:50px">Sr. No.</th>
            <th>Details of Goods</th>
            <th style="width:110px">HSN Code</th>
            <th style="width:80px">Pcs./Mtr</th>
            <th style="width:90px">Rate</th>
            <th style="width:130px">Total</th>
          </tr>
        </thead>

        <tbody>
          ${items
            .map(
              (item) => `
                <tr>
                  <td>${escapeHtml(item.serial)}</td>

                  <td>
                    D. No: ${escapeHtml(item.detail)}
                  </td>

                  <td>
                    ${escapeHtml(item.hsnCode || "5407")}
                  </td>

                  <td>
                    ${escapeHtml(item.quantity)}
                  </td>

                  <td>
                    ${escapeHtml(Number(item.price).toFixed(2))}
                  </td>

                  <td>
                    ${escapeHtml(Number(item.total).toFixed(2))}
                  </td>
                </tr>
              `
            )
            .join("")}

          ${Array.from({
            length: Math.max(0, 18 - items.length),
          })
            .map(
              () => `
                <tr>
                  <td>&nbsp;</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              `
            )
            .join("")}
        </tbody>
      </table>

      <div class="bill-bottom">
        <div class="bill-words">
          <b>Total Pcs.:</b> ${escapeHtml(totalQuantity)}
          <br />
          <b>Total amount in words</b>
          <br />
          ${escapeHtml(amountWords)} Only
        </div>

        <div class="bill-totals">
          <table>
            <tr>
              <td><b>Total</b></td>

              <td style="text-align:right">
                ${escapeHtml(subtotal.toFixed(2))}
              </td>
            </tr>

            ${
              discount > 0
                ? `
                  <tr>
                    <td>Discount${Number(quotation.discount_percentage) > 0 ? ` (${escapeHtml(String(Number(quotation.discount_percentage)))}%)` : ""}</td>

                    <td style="text-align:right">
                      -${escapeHtml(discount.toFixed(2))}
                    </td>
                  </tr>
                `
                : ""
            }

            ${
              grossAmount !== subtotal
                ? `
                  <tr>
                    <td><b>Gross Amount</b></td>

                    <td style="text-align:right">
                      <b>
                        ${escapeHtml(grossAmount.toFixed(2))}
                      </b>
                    </td>
                  </tr>
                `
                : ""
            }

            ${
              cgstAmount > 0
                ? `
                  <tr>
                    <td>Add : CGST 2.5%</td>

                    <td style="text-align:right">
                      ${escapeHtml(cgstAmount.toFixed(2))}
                    </td>
                  </tr>
                `
                : ""
            }

            ${
              sgstAmount > 0
                ? `
                  <tr>
                    <td>Add : SGST 2.5%</td>

                    <td style="text-align:right">
                      ${escapeHtml(sgstAmount.toFixed(2))}
                    </td>
                  </tr>
                `
                : ""
            }

            ${
              igstAmount > 0
                ? `
                  <tr>
                    <td>Add : IGST 5.0%</td>

                    <td style="text-align:right">
                      ${escapeHtml(igstAmount.toFixed(2))}
                    </td>
                  </tr>
                `
                : ""
            }

            ${
              showPackagingCharges && packaging > 0
                ? `
                  <tr>
                    <td>Add : Packaging</td>

                    <td style="text-align:right">
                      ${escapeHtml(packaging.toFixed(2))}
                    </td>
                  </tr>
                `
                : ""
            }

            ${
              transport > 0
                ? `
                  <tr>
                    <td>Add : Transportation</td>

                    <td style="text-align:right">
                      ${escapeHtml(transport.toFixed(2))}
                    </td>
                  </tr>
                `
                : ""
            }

            ${
              Number(quotation.round_off_amount || 0) !== 0
                ? `
                  <tr>
                    <td>Round Off</td>

                    <td style="text-align:right">
                      ${Number(quotation.round_off_amount || 0) > 0 ? "+" : ""}${escapeHtml(Number(quotation.round_off_amount || 0).toFixed(2))}
                    </td>
                  </tr>
                `
                : ""
            }

            <tr>
              <td><b>Total</b></td>

              <td style="text-align:right">
                <b>
                  ${escapeHtml(grandTotal.toFixed(2))}
                </b>
              </td>
            </tr>
          </table>
        </div>
      </div>

      <div class="bill-bank-sign">
        <div class="bill-bank">
          <div>
            <b>${escapeHtml(branding.bankName || "BANK")}</b>
            : A/c No.
            ${escapeHtml(branding.bankAccountNumber || "")}
          </div>

          <div style="margin-top:6px;">
            <b>IFSC Code :</b>
            ${escapeHtml(branding.bankIfscCode || "")}
          </div>
        </div>

        <div class="bill-sign">
          For,
          <b>
            ${escapeHtml(branding.brandName || "NILAM SYNTHETICS")}
          </b>

          <br /><br /><br />

          Authorised Signature
        </div>
      </div>

      <div class="bill-terms">
        <b>Terms & Conditions :</b>

        <br />

        (1) Subject to Ahmedabad Jurisdiction.

        <br />

        (2) We are not responsible for any kind of transit loss.
      </div>
    </div>
  </body>
</html>`;
};
